/**
 * 监听群聊自定义消息，收到抢单类型消息时弹出全局抢单弹窗
 * 支持格式: message_type='add_order_desired' 或 businessID='grab_order'
 * payload.data 结构: {"data":{...},"cmd":"CustomCmdMsg"} 或直接 {...}
 */
import eventBus from '@/utils/eventBus'
import { TUIStore, StoreName } from '@tencentcloud/chat-uikit-engine-lite'
import { queryConfig } from '@/api/home'
import store from '@/store'

// loseDesiredDuration 配置缓存（秒），默认 600，仅对此时长内的新消息弹窗
let cachedLoseDesiredSeconds = 600

function fetchLoseDesiredDuration() {
  queryConfig({ configGroup: 'order', configName: 'loseDesiredDuration' })
    .then((res) => {
      if (res?.code === 1 && res?.data?.configValue != null) {
        const v = Number(res.data.configValue)
        if (!Number.isNaN(v) && v > 0) cachedLoseDesiredSeconds = v
      }
    })
    .catch(() => {})
}

function parsePayloadData(payload) {
  if (!payload) return null
  let data = payload.data
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data)
    } catch (_e) {
      return null
    }
  }
  // 支持嵌套: { data: { id, userName, ... }, cmd }
  const inner = data && data.data ? data.data : data
  return inner
}

function isGrabOrderMessage(inner) {
  if (!inner) return false
  return (
    inner.message_type === 'add_order_desired' ||
    inner.businessID === 'grab_order'
  )
}

function buildPayloadToShow(inner) {
  const desiredId = inner.id ?? inner.desiredId ?? inner.desired_id
  if (!desiredId) return null
  return {
    desiredId: String(desiredId),
    nickname: inner.userName ?? inner.nickname ?? inner.nick,
    avatar: inner.userAvatar ?? inner.avatar ?? inner.faceUrl,
    sex: inner.userSex ?? inner.sex,
    age: inner.userAge ?? inner.age,
    amount: inner.allDiamondNum ?? inner.allMoney ?? inner.amount ?? inner.totalPrice ?? inner.total,
    startTime: inner.expectStartTime ?? inner.startTime,
    game: inner.gameName ?? inner.game,
    serviceType: inner.skillTypeName ?? inner.serviceType ?? inner.rank,
    gameRegion: inner.gameRegion ?? inner.region,
    remark: inner.descr ?? inner.remark,
  }
}

function showGrabDialog(inner) {
  const payload = buildPayloadToShow(inner)
  if (payload) eventBus.$emit('show-grab-order-dialog', payload)
}

function parseCloudCustomData(msg) {
  let str = msg?.cloudCustomData || msg?.payload?.cloudCustomData || ''
  if (!str && msg?.payload?.data) {
    const d = msg.payload.data
    str = typeof d === 'string' ? d : (typeof d === 'object' && d.data ? d.data : '')
  }
  if (typeof str !== 'string') return null
  try {
    return JSON.parse(str)
  } catch (_) {
    return null
  }
}

// 通过 TUIStore 监听会话列表，当 lastMessage 含 add_order_desired 时弹窗；C2C 含 msgType=21 时触发匹配成功
export function watchConversationListForGrabOrder() {
  fetchLoseDesiredDuration()
  const shownKeys = new Set()
  const matchSuccessShownKeys = new Set()
  const unwatch = TUIStore.watch(StoreName.CONV, {
    conversationList: (list) => {
      if (!Array.isArray(list)) return
      const now = Math.floor(Date.now() / 1000)
      list.forEach((conv) => {
        const lastMsg = conv.lastMessage || conv._conversation?.lastMessage
        if (!lastMsg || lastMsg.type !== 'TIMCustomElem') return

        const customJson = parseCloudCustomData(lastMsg)
        const isC2C = conv.type === 'C2C' || (conv.conversationID || '').startsWith('C2C')

        // C2C 抢单成功：msgType=21，content=大神用户ID，userId=下单用户；仅下单用户弹窗
        if (isC2C && customJson && String(customJson.msgType) === '21' && customJson.content) {
          const orderPlacerId = String(customJson.userId || '').trim()
          const userInfo = store?.getters?.['user/userInfo'] || JSON.parse(localStorage.getItem('userInfo') || 'null') || {}
          const currentUserId = String(userInfo.userID || userInfo.userId || userInfo.id || userInfo.uid || '').trim()
          if (orderPlacerId && currentUserId && orderPlacerId !== currentUserId) return
          const msgTime = lastMsg.lastTime || lastMsg.time || 0
          if (now - msgTime > cachedLoseDesiredSeconds) return
          const key = `${conv.conversationID}_${msgTime}_${customJson.content}`
          if (!matchSuccessShownKeys.has(key)) {
            matchSuccessShownKeys.add(key)
            if (matchSuccessShownKeys.size > 100) matchSuccessShownKeys.clear()
            eventBus.$emit('match-success-msg21', { masterUserId: customJson.content })
          }
          return
        }

        if (conv.type !== 'GROUP' && !(conv.conversationID || '').startsWith('GROUP')) return
        const payload = lastMsg.payload || {}
        const inner = parsePayloadData(payload)
        if (!isGrabOrderMessage(inner)) return
        const key = `${conv.conversationID}_${lastMsg.lastTime}_${inner.id}`
        if (shownKeys.has(key)) return
        if (now - (lastMsg.lastTime || 0) > cachedLoseDesiredSeconds) return
        shownKeys.add(key)
        if (shownKeys.size > 100) shownKeys.clear()
        showGrabDialog(inner)
      })
    },
  })
  return unwatch
}

export function registerGrabOrderListener(chat) {
  watchConversationListForGrabOrder()
  if (!chat || typeof chat.on !== 'function') return
  const tim = chat
  try {
    const EVENT = tim.TIM?.EVENT || tim.EVENT || {}
    const eventName = EVENT.MESSAGE_RECEIVED || 'TIM.EVENT.MESSAGE_RECEIVED' || 'MESSAGE_RECEIVED'
    const CONV_GROUP = tim.TIM?.TYPES?.CONV_GROUP ?? 2
    const MSG_CUSTOM = tim.TIM?.TYPES?.MSG_CUSTOM ?? 8

    const CONV_C2C = tim.TIM?.TYPES?.CONV_C2C ?? 1
    const handler = (event) => {
      const list = event?.data || (Array.isArray(event) ? event : [event])
      if (!Array.isArray(list)) return
      list.forEach((msg) => {
        try {
          const convType = msg.conversationType ?? msg.conversation_type
          const msgType = msg.type ?? msg.messageType
          if (msgType !== MSG_CUSTOM && msgType !== 'TIMCustomElem') return

          const payload = msg.payload || msg.getMessage?.()?.payload || {}
          const msgObj = msg.getMessage ? msg.getMessage() : msg
          const customJson = parseCloudCustomData(msgObj || msg)

          // C2C 抢单成功：msgType=21，content=大神用户ID，userId=下单用户；仅下单用户弹窗
          if (convType === CONV_C2C || convType === 'C2C') {
            if (customJson && String(customJson.msgType) === '21' && customJson.content) {
              const orderPlacerId = String(customJson.userId || '').trim()
              const userInfo = store?.getters?.['user/userInfo'] || JSON.parse(localStorage.getItem('userInfo') || 'null') || {}
              const currentUserId = String(userInfo.userID || userInfo.userId || userInfo.id || userInfo.uid || '').trim()
              if (orderPlacerId && currentUserId && orderPlacerId !== currentUserId) return
              eventBus.$emit('match-success-msg21', { masterUserId: customJson.content })
            }
            return
          }

          if (convType !== CONV_GROUP && convType !== 'GROUP') return
          const inner = parsePayloadData(payload)
          if (!isGrabOrderMessage(inner)) return

          showGrabDialog(inner)
        } catch (_e) {
          // ignore parse errors
        }
      })
    }

    tim.on(eventName, handler)
    return () => tim.off?.(eventName, handler)
  } catch (e) {
    console.warn('[grabOrderListener] register failed:', e)
    return null
  }
}
